import React, { useState } from 'react'
import { backupsAPI } from '@hpanel/web-common/api/client'

interface Props {
  backupId: number
  onError: (msg: string) => void
}

/**
 * Triggers a blob download for a completed backup.
 *
 * Uses backupsAPI.download() which routes through fetchAPIBlob — never calls
 * .json() on the gzip stream, and applies full §9 error handling.
 *
 * On success, creates an object URL and clicks a hidden <a> to trigger the
 * browser's Save dialog with the server-provided filename.
 */
export const DownloadBackupButton: React.FC<Props> = ({ backupId, onError }) => {
  const [downloading, setDownloading] = useState(false)

  const handleClick = async () => {
    setDownloading(true)
    try {
      const { blob, filename } = await backupsAPI.download(backupId)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch (e: unknown) {
      onError(e instanceof Error ? e.message : 'Download failed')
    } finally {
      setDownloading(false)
    }
  }

  return (
    <button
      data-testid={`backups-download-${backupId}`}
      onClick={() => void handleClick()}
      disabled={downloading}
      className="px-2 py-1 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-xs rounded"
    >
      {downloading ? 'Downloading…' : 'Download'}
    </button>
  )
}
